/**
 * OnboardingChecklist (TASK-659, upgraded to the 12-step spine in TASK-537).
 *
 * Renders the onboarding mission aggregate (TASK-536): every step's status is
 * derived server-side from the same canonical sources the full pages use — no
 * parallel truth. Each step shows its actor, status, and next permitted
 * action; hard-gate actions (convert, bank submission) are explicit confirms.
 */
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, Clock, Circle, AlertCircle } from 'lucide-react';
import {
  useOnboardingMission,
  recordBankSubmission,
  recordBankAccount,
  type OnboardingStep,
} from '../../../hooks/useOnboardingMission';
import screeningService from '../../../services/screeningService';
import { httpClient } from '../../../lib/httpClient';
import { requestCopilotPrompt } from '../../dock/requestCopilotPrompt';
import { StatusBadge } from '../../ui/status-badge';
import { WidgetContainer } from '../WidgetContainer';

const ACTOR_LABELS: Record<OnboardingStep['actor'], string> = {
  rm: 'You',
  agent: 'Kielis',
  client: 'Client',
  co: 'Compliance',
  bank: 'Custodian',
};

const STEP_ICONS: Record<OnboardingStep['status'], typeof Check> = {
  done: Check,
  attention: AlertCircle,
  waiting: Clock,
  open: Circle,
};

const STEP_ICON_CLASS: Record<OnboardingStep['status'], string> = {
  done: 'text-status-success-fg',
  attention: 'text-status-attention-fg',
  waiting: 'text-status-info-fg',
  open: 'text-muted-foreground/50',
};

export function OnboardingChecklist({
  clientId,
  clientName,
}: {
  clientId: string;
  clientName?: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const mission = useOnboardingMission(clientId);
  const [converting, setConverting] = useState(false);
  const [screeningRunning, setScreeningRunning] = useState(false);
  const [bankForm, setBankForm] = useState<'submission' | 'account' | null>(null);
  const [custodian, setCustodian] = useState('');
  const [accountNumber, setAccountNumber] = useState('');

  const data = mission.data;
  const name = clientName || data?.client.name || 'Client';
  const isProspect = data?.client.status === 'prospect';
  const step = (key: string) => data?.steps.find((s) => s.key === key);
  const gatesMet =
    step('risk_profile')?.status === 'done' &&
    step('co_review')?.status === 'done' &&
    step('screening')?.status === 'done';

  const runScreening = async () => {
    setScreeningRunning(true);
    try {
      await screeningService.screenClient(clientId);
      toast.success('Screening complete');
    } catch {
      toast.error('Screening failed — try again or check the AML service');
    } finally {
      setScreeningRunning(false);
      mission.invalidate();
    }
  };

  const submitBankForm = async () => {
    try {
      if (bankForm === 'submission') {
        await recordBankSubmission(clientId, { custodian });
        toast.success(`Pack submission to ${custodian} recorded`);
      } else if (bankForm === 'account') {
        await recordBankAccount(clientId, { account_number: accountNumber });
        toast.success('Custodian account recorded');
      }
      setBankForm(null);
      setCustodian('');
      setAccountNumber('');
      mission.invalidate();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: { message?: string } } } })
        ?.response?.data?.detail;
      toast.error(detail?.message ?? 'Could not record — check the blockers');
    }
  };

  const convert = async () => {
    setConverting(true);
    try {
      await httpClient.post(`/api/clients/${clientId}/convert`, {});
      toast.success(`${name} converted to client`);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: { message?: string } } } })
        ?.response?.data?.detail;
      toast.error(detail?.message ?? 'Conversion blocked — mandatory gates are not met');
    } finally {
      setConverting(false);
      mission.invalidate();
    }
  };

  // Next permitted action per step — the mission stays read-only about state;
  // every mutation goes through the real surface it belongs to.
  const stepAction = (s: OnboardingStep): { label: string; onClick: () => void } | undefined => {
    switch (s.action) {
      case 'request_documents':
        return { label: 'Request documents', onClick: () => navigate(`/clients/${clientId}?tab=documents`) };
      case 'review_quarantine':
        return { label: 'Review uploads', onClick: () => navigate('/documents?tab=quarantine') };
      case 'open_kyc_workspace':
        return { label: 'Open KYC workspace', onClick: () => navigate(`/clients/${clientId}/kyc-workspace`) };
      case 'open_risk_profile':
        return { label: 'Open risk profile', onClick: () => navigate(`/clients/${clientId}?tab=compliance`) };
      case 'run_screening':
        return { label: screeningRunning ? 'Screening…' : 'Run screening', onClick: runScreening };
      case 'open_workbench':
        // `/workbench` is not a route — the real one is `/workbench/:docId`, so this button
        // navigated nowhere and the step looked broken (reported live 2026-08-24). It is only
        // offered when the mission names the document it opens.
        return s.document_id
          ? { label: 'Open pack', onClick: () => navigate(`/workbench/${s.document_id}`) }
          : undefined;
      case 'prepare_pack':
        // Nothing exists to open yet, so the action is to CREATE it — handed to the copilot
        // with the client already in it, which is where document generation lives.
        //
        // Routed through the shared helper: this widget renders on the dashboard canvas as
        // well as in the CanvasDrawer, and the two placements have different copilots.
        // Handing it to `dockStore` unconditionally sent it to a dock that never mounts on
        // /dashboard, so the button did nothing at all (reported live 2026-08-24).
        return {
          label: 'Prepare pack',
          onClick: () => {
            const sent = requestCopilotPrompt(
              `Prepare the account-opening pack for ${name}: the mandate and account-opening ` +
                `forms, using what is already on file.`,
              location.pathname
            );
            if (!sent) toast.error('Open the copilot here to prepare the pack');
          },
        };
      case 'record_bank_submission':
        return { label: 'Record submission', onClick: () => setBankForm('submission') };
      case 'record_bank_account':
        return { label: 'Record account', onClick: () => setBankForm('account') };
      case 'create_portfolio':
        return { label: 'Create portfolio', onClick: () => navigate(`/clients/${clientId}?tab=portfolios`) };
      default:
        return undefined; // 'convert' is the dedicated gate button below
    }
  };

  return (
    <WidgetContainer title={`Onboarding — ${name}`} source="Onboarding mission · canonical records">
      {mission.isLoading && <div className="h-40 animate-pulse rounded-xl bg-accent" />}
      {mission.isError && (
        <p className="text-[13px] text-destructive">
          Could not load this client's onboarding state.
        </p>
      )}
      {data && (
        <div data-testid="onboarding-checklist" className="space-y-2">
          <div className="flex items-center justify-between">
            <StatusBadge status={isProspect ? 'attention' : 'success'} size="sm">
              {isProspect ? 'Prospect — limited actions' : 'Client — fully onboarded'}
            </StatusBadge>
            <span className="text-[11px] text-muted-foreground">
              {data.steps.filter((s) => s.status === 'done').length}/{data.steps.length} steps
            </span>
          </div>

          <ol className="space-y-1">
            {data.steps.map((s) => {
              const Icon = STEP_ICONS[s.status];
              const action = stepAction(s);
              return (
                <li
                  key={s.key}
                  data-testid={`onboarding-step-${s.key}`}
                  className="flex items-center gap-2.5 rounded-lg border border-border px-3 py-1.5"
                >
                  <Icon className={`h-4 w-4 shrink-0 ${STEP_ICON_CLASS[s.status]}`} aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-medium text-foreground">
                      {s.label}
                      <span className="ml-2 text-[11px] font-normal text-muted-foreground">
                        {s.waiting_on ? `Waiting on ${s.waiting_on}` : ACTOR_LABELS[s.actor]}
                      </span>
                    </p>
                    {s.detail && (
                      <p className="truncate text-[11px] text-muted-foreground">{s.detail}</p>
                    )}
                  </div>
                  {action && s.status !== 'done' && (
                    <button
                      type="button"
                      onClick={action.onClick}
                      disabled={s.action === 'run_screening' && screeningRunning}
                      className="shrink-0 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-foreground hover:bg-accent disabled:opacity-50"
                    >
                      {action.label}
                    </button>
                  )}
                </li>
              );
            })}
          </ol>

          {bankForm && (
            <div className="space-y-2 rounded-lg border border-border bg-accent/40 p-3">
              <p className="text-[12px] font-medium text-foreground">
                {bankForm === 'submission'
                  ? 'Record the pack submission — this confirms client data left the firm to the custodian.'
                  : 'Record the account number returned by the custodian.'}
              </p>
              {bankForm === 'submission' ? (
                <input
                  value={custodian}
                  onChange={(e) => setCustodian(e.target.value)}
                  placeholder="Custodian bank name"
                  aria-label="Custodian bank name"
                  className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-[13px]"
                />
              ) : (
                <input
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder="Account number (IBAN or custodian reference)"
                  aria-label="Account number"
                  className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-[13px]"
                />
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setBankForm(null)}
                  className="rounded-md px-2.5 py-1.5 text-[12px] text-muted-foreground hover:bg-accent"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitBankForm}
                  disabled={bankForm === 'submission' ? !custodian.trim() : !accountNumber.trim()}
                  data-testid="bank-form-confirm"
                  className="rounded-md bg-primary px-2.5 py-1.5 text-[12px] font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
                >
                  Confirm
                </button>
              </div>
            </div>
          )}

          {isProspect && (
            <div className="flex items-center justify-between gap-3 border-t border-border pt-3">
              <p className="text-[12px] text-muted-foreground">
                {gatesMet
                  ? 'All mandatory gates passed — this prospect can become a client.'
                  : 'Conversion unlocks when KYC is approved, the risk profile is completed, and screening is clear.'}
              </p>
              <button
                type="button"
                onClick={convert}
                disabled={converting || !gatesMet}
                data-testid="convert-to-client"
                title={
                  gatesMet
                    ? 'Convert this prospect to a client'
                    : 'Blocked: KYC approval, a completed risk profile, and clear screening are required'
                }
                className="shrink-0 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {converting ? 'Converting…' : 'Convert to client'}
              </button>
            </div>
          )}
        </div>
      )}
    </WidgetContainer>
  );
}
